import React, { useState, useEffect } from 'react';
import { UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import Select from "react-select";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { getOperatorsForType, getInputType, EXCLUDED_TYPES } from './operatorsByType';

const TOKEN = document.querySelector("[name='csrf-token']")?.content;

const COMPARE_MODES = [
  { value: 'value', label: 'Valor fijo' },
  { value: 'question', label: 'Pregunta del mismo registro' },
  { value: 'parent_source', label: 'Pregunta del formato fuente (padre de los items)' },
  { value: 'parent_current', label: 'Pregunta del formato actual (el que dispara)' },
];

export default function FormCreate({ isOpen, target, format_duplication, modeEdit, condition, onCreated, onUpdated, onClose, existingConditionsCount = 0, formatId }) {
  const [questions, setQuestions] = useState([]);
  const [parentSourceQuestions, setParentSourceQuestions] = useState([]);
  const [parentCurrentQuestions, setParentCurrentQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(
    modeEdit && condition ? { label: condition.question_info.name, value: condition.question_info.id, question_type: condition.question_info.type } : null
  );
  const [operation, setOperation] = useState(modeEdit && condition ? condition.operation : '');
  const [value, setValue] = useState(modeEdit && condition ? (condition.value || '') : '');
  const [valueTo, setValueTo] = useState('');
  const [conditional, setConditional] = useState(modeEdit && condition ? condition.conditional : 'Y');
  const [compareMode, setCompareMode] = useState(modeEdit && condition ? (condition.compare_mode || 'value') : 'value');
  const [selectedQuestionToCompare, setSelectedQuestionToCompare] = useState(
    modeEdit && condition && condition.question_to_compare_info
      ? { label: condition.question_to_compare_info.name, value: condition.question_to_compare_info.id }
      : null
  );

  // Load subform source questions
  useEffect(() => {
    fetch(`/formatos/data_questions/${format_duplication.format_subform_source.id}`, {
      method: 'GET', headers: { "X-CSRF-Token": TOKEN, "Content-Type": "application/json" }
    })
      .then(r => r.json())
      .then(data => {
        setQuestions(data.steps_questions
          .filter(q => !EXCLUDED_TYPES.includes(q.question_type))
          .map(q => ({ label: q.question, value: q.id, question_type: q.question_type })));
      });

    // Load parent source questions (formato fuente)
    fetch(`/formatos/data_questions/${format_duplication.format_source.id}`, {
      method: 'GET', headers: { "X-CSRF-Token": TOKEN, "Content-Type": "application/json" }
    })
      .then(r => r.json())
      .then(data => {
        setParentSourceQuestions(data.steps_questions
          .filter(q => !EXCLUDED_TYPES.includes(q.question_type))
          .map(q => ({ label: q.question, value: q.id, question_type: q.question_type })));
      });

    // Load parent current questions (formato actual)
    if (formatId) {
      fetch(`/formatos/data_questions/${formatId}`, {
        method: 'GET', headers: { "X-CSRF-Token": TOKEN, "Content-Type": "application/json" }
      })
        .then(r => r.json())
        .then(data => {
          setParentCurrentQuestions(data.steps_questions
            .filter(q => !EXCLUDED_TYPES.includes(q.question_type))
            .map(q => ({ label: q.question, value: q.id, question_type: q.question_type })));
        });
    }
  }, []);

  useEffect(() => {
    if (modeEdit && condition && condition.operation === 'entre' && condition.value) {
      const parts = condition.value.split(',');
      if (parts.length === 2) { setValue(parts[0]); setValueTo(parts[1]); }
    }
  }, []);

  const operators = selectedQuestion ? getOperatorsForType(selectedQuestion.question_type) : [];
  const inputType = selectedQuestion ? getInputType(selectedQuestion.question_type) : 'text';

  const handleQuestionChange = (opt) => {
    setSelectedQuestion(opt);
    setOperation('');
    setValue('');
    setValueTo('');
    setCompareMode('value');
    setSelectedQuestionToCompare(null);
  };

  // Get comparison questions based on compare mode
  const getCompareQuestions = () => {
    let source;
    switch (compareMode) {
      case 'question': source = questions; break;
      case 'parent_source': source = parentSourceQuestions; break;
      case 'parent_current': source = parentCurrentQuestions; break;
      default: return [];
    }
    if (!selectedQuestion) return source;
    return source.filter(q => q.question_type === selectedQuestion.question_type);
  };

  const handleSave = () => {
    if (!selectedQuestion || !operation) return;
    if (compareMode === 'value' && !value && operation !== 'entre') return;
    if (compareMode !== 'value' && !selectedQuestionToCompare) return;

    const finalValue = operation === 'entre' ? `${value},${valueTo}` : value;
    const body = {
      format_duplication_id: format_duplication.id,
      question_id: selectedQuestion.value,
      operation,
      value: compareMode === 'value' ? finalValue : null,
      conditional,
      compare_mode: compareMode,
      question_to_compare_id: compareMode !== 'value' ? selectedQuestionToCompare.value : null,
    };

    const url = modeEdit ? `/formatos/format_duplication_conditions/${condition.id}` : '/formatos/format_duplication_conditions';
    const method = modeEdit ? 'PATCH' : 'POST';

    fetch(url, { method, body: JSON.stringify(body), headers: { "X-CSRF-Token": TOKEN, "Content-Type": "application/json" } })
      .then(r => r.json())
      .then(data => {
        Swal.fire({ position: "center", type: data.type, title: data.success, showConfirmButton: false, timer: 1500 });
        if (modeEdit && onUpdated) onUpdated(data.register);
        else if (onCreated) onCreated(data.register);
        onClose();
      });
  };

  const compareModeLabel = {
    question: 'Pregunta del mismo registro (subform)',
    parent_source: 'Pregunta del formato fuente',
    parent_current: 'Pregunta del formato actual',
  };

  return (
    <UncontrolledPopover isOpen={isOpen} trigger="legacy" placement="botton" target={target} onClick={e => e.stopPropagation()}>
      <PopoverHeader>
        <div className='ui-modals-header-title__container'>
          <span className='ui-titles_modals'>
            {modeEdit ? "Editar condicion" : "Nueva condicion"}
            <a className="close-popover" onClick={onClose}><i className="fas fa-times" /></a>
          </span>
        </div>
      </PopoverHeader>
      <PopoverBody className="popover-column">
        <div className="col-md-12 p-0" style={{ width: 380, height: "auto" }}>
          {(existingConditionsCount > 0 || modeEdit) && (
            <div className="col-md-12 mb-2 p-0">
              <label>Conector logico</label>
              <select className="form-control form-control-sm" value={conditional} onChange={e => setConditional(e.target.value)}>
                <option value="Y">Y (AND)</option>
                <option value="O">O (OR)</option>
              </select>
            </div>
          )}

          <div className="col-md-12 mb-2 p-0">
            <label>Pregunta a evaluar</label>
            <Select options={questions} value={selectedQuestion} onChange={handleQuestionChange} placeholder="Seleccione..." className="link-form" />
            <span style={{ fontSize: 10, color: '#999' }}>Campo del subformulario fuente que se evaluara</span>
          </div>

          {selectedQuestion && (
            <div className="col-md-12 mb-2 p-0">
              <label>Operador</label>
              <select className="form-control form-control-sm" value={operation} onChange={e => setOperation(e.target.value)}>
                <option value="">Seleccione...</option>
                {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
            </div>
          )}

          {selectedQuestion && operation && (
            <div className="col-md-12 mb-2 p-0">
              <label>Comparar con</label>
              <select className="form-control form-control-sm" value={compareMode} onChange={e => {
                setCompareMode(e.target.value);
                setValue(''); setValueTo(''); setSelectedQuestionToCompare(null);
              }}>
                {COMPARE_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          )}

          {selectedQuestion && operation && compareMode === 'value' && (
            operation === 'entre' ? (
              <div className="col-md-12 mb-2 p-0">
                <label>Rango</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control form-control-sm" type={inputType} value={value} onChange={e => setValue(e.target.value)} placeholder="Desde" />
                  <input className="form-control form-control-sm" type={inputType} value={valueTo} onChange={e => setValueTo(e.target.value)} placeholder="Hasta" />
                </div>
              </div>
            ) : (
              <div className="col-md-12 mb-2 p-0">
                <label>Valor</label>
                <input className="form-control form-control-sm" type={inputType} value={value} onChange={e => setValue(e.target.value)} placeholder="Valor a comparar..." />
              </div>
            )
          )}

          {selectedQuestion && operation && compareMode !== 'value' && (
            <div className="col-md-12 mb-2 p-0">
              <label>{compareModeLabel[compareMode]}</label>
              <Select
                options={getCompareQuestions()}
                value={selectedQuestionToCompare}
                onChange={setSelectedQuestionToCompare}
                placeholder="Seleccione pregunta..."
                className="link-form"
              />
              <span style={{ fontSize: 10, color: '#999' }}>Solo preguntas del mismo tipo ({selectedQuestion.question_type})</span>
            </div>
          )}

          <div className="col-md-12 p-0">
            <button className="btn-shadow btn btn-info btn-block" onClick={handleSave} disabled={!selectedQuestion || !operation}>
              {modeEdit ? "Actualizar" : "Agregar condicion"}
            </button>
          </div>
        </div>
      </PopoverBody>
    </UncontrolledPopover>
  );
}
